import React, { useContext } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { SiteContext, selectUser } from '@homeplay/app';
import {
  formatBalance,
  formatBonusCampaignDate,
  getBonusCampaignDaysLeft,
} from '@homeplay/utils';
import {
  BonusCampaignLabels,
  BonusCampaignProgress,
  useTimeLeft,
} from './BonusCampaignParts';
import {
  getBonusCampaignBanner,
  getPassedBonusStatusText,
  getPromoPath,
  getSitePath,
} from './helpers';
import classes from './BonusCampaigns.module.scss';

const BonusCampaignCard = ({ bonusCampaign, isPassed = false }) => {
  const { siteName } = useContext(SiteContext);
  const user = useSelector(selectUser);
  const navigate = useNavigate();
  const bonusState = bonusCampaign.bonus_state || {};
  const status = user ? bonusState.status : null;
  const timeLeft = useTimeLeft(
    status === 'pending' ? bonusCampaign.end_timestamp : null
  );

  const openDetails = () => {
    if (!isPassed) {
      navigate(getPromoPath(siteName, bonusCampaign.id));
    }
  };

  const renderAction = (text, path) => (
    <span
      className={classes.CardButton + ' button-primary'}
      onClick={(e) => {
        e.stopPropagation();
        navigate(path);
      }}
    >
      {text}
    </span>
  );

  const renderInfo = (title, value, isHighlighted = false) => (
    <div className={classes.CardInfo}>
      <span className={isHighlighted ? 'color-primary' : ''}>{title}</span>
      <strong>{value}</strong>
    </div>
  );

  const renderFooter = () => {
    if (isPassed) {
      return (
        <>
          {bonusState.status === 'success' &&
            renderInfo(
              'Converted to cash',
              formatBalance(bonusState.converted_to_cash)
            )}
          {renderInfo(
            getPassedBonusStatusText(bonusState.status),
            formatBonusCampaignDate(bonusState.end_timestamp, 'dd.MM.yyyy')
          )}
        </>
      );
    }

    switch (status) {
      case 'active':
        return (
          <>
            {renderInfo(
              'Active',
              `${getBonusCampaignDaysLeft(
                bonusState.expiration_timestamp
              )} days left`,
              true
            )}
            {renderAction('Play', getSitePath(siteName))}
          </>
        );
      case 'pending':
        return (
          <>
            {renderInfo('Waiting for deposit', timeLeft, true)}
            {renderAction('Deposit', getSitePath(siteName, 'cashin'))}
          </>
        );
      default:
        return (
          <>
            {renderInfo(
              'Expiry',
              formatBonusCampaignDate(bonusCampaign.end_timestamp)
            )}
            <span className={classes.CardButton + ' button-primary'}>More</span>
          </>
        );
    }
  };

  return (
    <div
      className={classes.Card + (isPassed ? ' ' + classes.Passed : '')}
      onClick={openDetails}
      role={isPassed ? undefined : 'link'}
    >
      <div className={classes.CardBanner}>
        <img src={getBonusCampaignBanner(bonusCampaign)} alt={bonusCampaign.name} />
        <BonusCampaignLabels type={bonusCampaign.type} />
      </div>

      <div className={classes.CardBody}>
        <h3>{bonusCampaign.name}</h3>
        <div
          className={classes.CardDescription}
          dangerouslySetInnerHTML={{ __html: bonusCampaign.short_description }}
        />

        {status === 'active' && !isPassed && (
          <BonusCampaignProgress percentage={bonusState.completion_percentage} />
        )}

        <div className={classes.CardFooter}>{renderFooter()}</div>
      </div>
    </div>
  );
};

export default BonusCampaignCard;
